// Bileli: pre-login language (ру⇄кыр) + theme (dark/light) switchers, floated top-right over EVERY auth
// sub-screen (phone/QR/code/password/register). Mounted once in Auth.tsx as a sibling of the <Transition>
// inside .AuthLayout — one seam, no per-screen edits (mirrors the BileliAuthPromo pattern). why here and
// not in the promo: the promo is desktop-only (<1024px hidden), these controls must exist on mobile too.
import type { FC } from '../../lib/teact/teact';
import { memo } from '../../lib/teact/teact';
import { getActions, withGlobal } from '../../global';

import type { ThemeKey } from '../../types';

import { selectSharedSettings } from '../../global/selectors/sharedState';
import buildClassName from '../../util/buildClassName';
import { bileliLangStore, setBileliLang } from '../../bileli/shared/i18n/langStore';
import { setBileliTheme } from '../../bileli/shared/lib/themeStore';
import { useStoreState } from '../../bileli/shared/lib/zustandTeact';

import Icon from '../../bileli/shared/ui/Icon';

import './BileliAuthControls.scss';

type StateProps = {
  theme: ThemeKey;
};

const BileliAuthControls: FC<StateProps> = ({ theme }) => {
  const { setSharedSettingOption } = getActions();

  const bileliLang = useStoreState(bileliLangStore, (s) => s.lang);

  const handleLang = (next: 'ru' | 'ky') => {
    // Bileli half only — there is no Telegram 'ky' langpack; the auth screens localize via the
    // useBileliAuthKy() overlay and the post-auth workspace remounts on this store (key={lang}).
    setBileliLang(next);
  };

  const handleTheme = (next: ThemeKey) => {
    // Same combined pattern as useProfile.setTheme: Bileli surface + Telegram chrome + pin the choice
    // (shouldUseSystemTheme:false makes ui/initial.ts keep it across reloads instead of re-deriving OS).
    setBileliTheme(next);
    setSharedSettingOption({ theme: next });
    setSharedSettingOption({ shouldUseSystemTheme: false });
  };

  return (
    <div className="BileliAuthControls">
      <div className="BileliAuthControls__seg" role="group" aria-label="Тил / Язык">
        <button
          type="button"
          className={buildClassName('BileliAuthControls__btn', bileliLang === 'ru' && 'active')}
          onClick={() => handleLang('ru')}
        >
          Рус
        </button>
        <button
          type="button"
          className={buildClassName('BileliAuthControls__btn', bileliLang === 'ky' && 'active')}
          onClick={() => handleLang('ky')}
        >
          Кыр
        </button>
      </div>
      <div className="BileliAuthControls__seg" role="group" aria-label="Тема">
        <button
          type="button"
          className={buildClassName('BileliAuthControls__btn', theme === 'dark' && 'active')}
          aria-label="Тёмная тема"
          onClick={() => handleTheme('dark')}
        >
          <Icon name="moon" size={16} />
        </button>
        <button
          type="button"
          className={buildClassName('BileliAuthControls__btn', theme === 'light' && 'active')}
          aria-label="Светлая тема"
          onClick={() => handleTheme('light')}
        >
          <Icon name="sun" size={16} />
        </button>
      </div>
    </div>
  );
};

export default memo(withGlobal(
  (global): Complete<StateProps> => {
    return {
      theme: selectSharedSettings(global).theme,
    };
  },
)(BileliAuthControls));
